import { memo } from "react";
import { cn } from "../../../../lib/cn";
import { SelectDropdown, Button } from "../../../../components/shared";
import { CustomPopover } from "../../../../components/shared/Popover";
import { ProjectsIcon, PlusIcon, DoubleArrowIcon } from "../../../../assets/icons";
import { NO_PROJECT_LABEL } from "../constants";
import { SessionList } from "./SessionList";

export const DraftFlowSidebar = memo(
  ({
    showSidebar,
    onToggleSidebar,
    selectedProjectId,
    projectOptions,
    projectsLoading,
    onProjectChange,
    onCreateSession,
    creatingSession,
    onSessionSelect,
    isProjectPopoverOpen,
    setIsProjectPopoverOpen,
  }) => {
    return (
      <div
        className={cn(
          "border-r flex flex-col h-full overflow-hidden shrink-0 transition-all duration-300",
          showSidebar ? "w-[260px]" : "w-[60px]",
        )}
      >
        <div
          className={cn(
            "border-b border-surface-border bg-surface/50",
            showSidebar ? "p-3" : "px-2 pt-3 pb-2",
          )}
        >
          <div
            className={cn(
              "flex items-center",
              showSidebar ? "justify-between mb-2" : "justify-center mb-3",
            )}
          >
            {showSidebar ? (
              <p className="text-xs font-semibold text-mediumgrey">AI Chat</p>
            ) : null}
            <button
              type="button"
              onClick={onToggleSidebar}
              className="p-1.5 rounded-md hover:bg-surface-hover text-mediumgrey cursor-pointer"
              aria-label={
                showSidebar ? "Collapse chat sidebar" : "Expand chat sidebar"
              }
              title={showSidebar ? "Collapse sidebar" : "Expand sidebar"}
              aria-pressed={!showSidebar}
            >
              <DoubleArrowIcon
                width={14}
                height={14}
                className={cn(
                  "text-mediumgrey",
                  showSidebar ? "rotate-180" : "",
                )}
              />
            </button>
          </div>

          <div
            className={cn(
              "flex flex-col gap-2",
              !showSidebar && "items-center",
            )}
          >
            {showSidebar ? (
              <div className="rounded-lg border border-surface-border bg-white px-2 py-2">
                <SelectDropdown
                  options={[
                    { id: "", name: NO_PROJECT_LABEL },
                    ...projectOptions,
                  ]}
                  label="Select Project"
                  name="project"
                  value={selectedProjectId || ""}
                  onChange={(e) => {
                    onProjectChange(e);
                  }}
                  disabled={projectsLoading}
                  loading={projectsLoading}
                />
              </div>
            ) : (
              <CustomPopover
                open={isProjectPopoverOpen}
                setOpen={setIsProjectPopoverOpen}
                trigger={
                  <button
                    type="button"
                    className="inline-flex size-10 items-center justify-center rounded-lg border border-surface-border bg-white shadow-sm transition-colors hover:bg-surface"
                    aria-label="Select project"
                    title="Select project"
                  >
                    <ProjectsIcon
                      width={16}
                      height={16}
                      className="text-darkgrey"
                    />
                  </button>
                }
                placement="right-start"
              >
                <div className="p-2 w-[260px] bg-white rounded-lg shadow-xl border border-surface-border">
                  <SelectDropdown
                    options={[
                      { id: "", name: NO_PROJECT_LABEL },
                      ...projectOptions,
                    ]}
                    label="Search Project"
                    name="project"
                    value={selectedProjectId || ""}
                    onChange={(e) => {
                      onProjectChange(e);
                      setIsProjectPopoverOpen(false);
                    }}
                    disabled={projectsLoading}
                    loading={projectsLoading}
                  />
                </div>
              </CustomPopover>
            )}

            <Button
              className={cn(
                "justify-center gap-2 rounded-lg bg-primary hover:bg-primary/90 text-white shadow-sm h-9",
                showSidebar ? "w-full" : "size-10 p-0",
              )}
              size="sm"
              onClick={onCreateSession}
              disabled={creatingSession}
              title="New Chat"
              aria-label="New chat"
            >
              <PlusIcon width={14} height={14} />
              {showSidebar && <span>New Chat</span>}
            </Button>
          </div>
        </div>

        <div
          className={cn(
            "flex-1 overflow-y-auto p-2",
            !showSidebar && "hidden",
          )}
        >
          <SessionList onSelect={onSessionSelect} />
        </div>
      </div>
    );
  },
);

DraftFlowSidebar.displayName = "DraftFlowSidebar";
